import { formatCurrency } from "@/helpers/formatters";
import { queryKeys } from "@/services/queryKeyFactory";
import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { toast } from "sonner";
import razorPayCheckoutHandler from "@/helpers/razorPayCheckoutHandler";
import { apiErrorHandler } from "@/helpers/apiErrorHandler";
import { useQueryClient } from "@tanstack/react-query";
import ButtonLoader from "../ButtonLoader";
import { Badge, X } from "lucide-react";

export default function UpgradeModal({
    isOpen,
    onClose,
    plans,
    currentPlan,
    modalTitle = "Upgrade Your Plan",
    modalSubtitle = "Unlock more features by upgrading your plan",
}) {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loading, setLoading] = useState(false);

    const queryClient = useQueryClient();

    const onSuccessfulPayment = async () => {
        await Promise.all(
            [queryKeys.me(), queryKeys.planRules(), queryKeys.paymentHistory(), queryKeys.compsets()].map((queryKey) =>
                queryClient.invalidateQueries({ queryKey })
            )
        );
    };

    const paymentHandler = async () => {
        try {
            if (!selectedPlan) {
                toast.error("Select a plan to continue");
                return;
            }

            setLoading(true);

            const plan = plans?.find((p) => p?.id == selectedPlan?.id);

            const paymentStatus = await razorPayCheckoutHandler(
                plan?.amount,
                plan?.currency,
                plan?.id,
                plan?.name,
                plan?.type === "regular" ? plan?.type : undefined
            );

            if (paymentStatus) {
                await onSuccessfulPayment();
                onClose();
            }

        } catch (error) {
            apiErrorHandler(error);
        } finally {
            setLoading(false);
        }
    };

    // Reset selection when modal closes
    const handleOpenChange = (open) => {
        if (!open) {
            setSelectedPlan(null);
            onClose();
        }
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />

                <Dialog.Content className="fixed inset-0 z-50 w-screen h-screen bg-white p-8 overflow-y-auto focus:outline-none">

                    <Dialog.Title className="text-2xl font-semibold text-center">
                        {modalTitle}
                    </Dialog.Title>

                    <p className="text-center text-gray-500 mt-2">
                        {modalSubtitle}
                    </p>

                    {/* Plans */}
                    <div className="flex flex-wrap gap-6 mt-8 justify-center">
                        {plans?.length === 0 && (
                            <p className="text-gray-400 text-center w-full mt-12">
                                No plans available.
                            </p>
                        )}

                        {plans?.map((plan) => {
                            const isSelected = selectedPlan?.id === plan.id;
                            return (
                                <div
                                    key={plan?.id}
                                    onClick={() => setSelectedPlan(plan)}
                                    className={`cursor-pointer relative border rounded-xl p-6 transition-all flex flex-col justify-between w-full sm:w-[48%] lg:w-[30%]
                                        ${isSelected
                                            ? "border-blue-600 bg-blue-50 ring-2 ring-blue-200"
                                            : "border-gray-200 hover:border-blue-300"
                                        }`}
                                >
                                    {plan?.offers?.is_offer && (
                                        <div className="absolute top-3 right-3 z-10">
                                            <span className="bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded-md shadow-sm">
                                                {plan.offers.offer_percent}% OFF
                                            </span>
                                        </div>
                                    )}
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-lg font-semibold">{plan?.name}</h3>
                                            {/* {plan?.type && (
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${plan.type === 'subscription'
                                                    ? 'bg-blue-100 text-blue-700'
                                                    : 'bg-amber-100 text-amber-700'
                                                    }`}>
                                                    {plan.type === 'subscription' ? 'Subscription' : 'One-time'}
                                                </span>
                                            )} */}
                                        </div>

                                        {/* <p className="text-3xl font-bold mt-3">
                                            {formatCurrency(plan?.amount, plan?.currency, false)}
                                            <span className="text-sm text-gray-500">
                                                {" "}/ {plan?.duration_days} days
                                            </span>
                                        </p> */}
                                        <div className="mt-3">
                                            <div className="flex items-baseline gap-2">
                                                {/* Final Price */}
                                                <span className="text-3xl font-bold">
                                                    {formatCurrency(
                                                        plan?.offers?.is_offer ? plan?.offers?.offer_price : plan?.amount,
                                                        plan?.currency,
                                                        false
                                                    )}
                                                </span>

                                                <span className="text-sm text-gray-500">
                                                    / {plan?.period}
                                                </span>
                                            </div>

                                            {/* Original Price */}
                                            {plan?.offers?.is_offer && (
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-sm text-gray-400 line-through">
                                                        {formatCurrency(plan?.amount, plan?.currency, false)}
                                                    </span>

                                                    <span className="text-xs text-green-600 font-medium">
                                                        Save{" "}
                                                        {formatCurrency(
                                                            plan.amount - plan.offers.offer_price,
                                                            plan.currency,
                                                            false
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <ul className="mt-5 space-y-2 text-sm text-gray-600">
                                            {plan?.features?.map((feature, i) => (
                                                <li key={i} className="flex gap-2">
                                                    <span className="text-green-600">✓</span>
                                                    {feature}
                                                </li>
                                            ))}
                                            {plan?.excluded_features?.map((feature, idx) => (
                                                <li key={`ex-${idx}`} className="flex items-start gap-3 text-sm">
                                                    <X className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-400" />
                                                    <span className="text-muted-foreground/60 line-through">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <button
                                        className={`mt-6 w-full py-2 text-sm rounded-md transition ${isSelected
                                            ? "bg-blue-600 text-white"
                                            : "border border-gray-300 hover:bg-gray-100"
                                            }`}
                                    >
                                        {isSelected ? "Selected" : "Select Plan"}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 mt-8">
                        <Dialog.Close asChild>
                            <button
                                disabled={loading}
                                className="px-4 py-2 border rounded-md hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                        </Dialog.Close>

                        <button
                            disabled={!selectedPlan || loading}
                            className="px-5 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
                            onClick={paymentHandler}
                        >
                            <ButtonLoader
                                loading={loading}
                                text={"Continue"}
                                loadingText="Processing..."
                            />
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}